// A vessel's serial numbers (Info / Files › Infocenter › a vessel › Serial numbers, or /serial-numbers/<SAP>):
// the console devices and the engines with their part numbers and serials, kept as the yard's
// "SN - <SAP>.txt" in the vessel's Infocenter folder (infocenter/<year>/<SAP> - <NAME>/), where the
// Vessel Identification page and the crews find it. A saved file opens in the form to be changed; a new
// one starts from the boat's order (features/serialNumbers.ts snFromOrder), else the yard's standard list.
// The text below the form is the file as it will be saved. Reading needs production:read, saving
// production:write (as the rest of Info / Files).

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { OS_MODEL_KEY, OS_NAME_KEY, modelKey } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { useSeason } from '../season/SeasonContext.js';
import { useOrderSheet, useOverview } from '../features/hooks.js';
import {
  ENGINE_MAKERS,
  buildSnText,
  infocenterYear,
  newEngine,
  parseSnText,
  snFileName,
  snFromOrder,
  snKey,
  useSaveSnFile,
  useSnText,
  useSnVessels,
  vesselFolderName,
  type SnBrand,
  type SnDevice,
  type SnEngine,
} from '../features/serialNumbers.js';
import { ErrorState, InlineError, LoadingState, PageHeader, useConfirm } from '../components/ui.js';
import { IconChevronRight, IconDownload, IconTrash } from '../components/icons.js';
import { useDateFormatter } from '../lib/format.js';

interface Form { model: string; vesselName: string; brand: SnBrand | ''; devices: SnDevice[]; engines: SnEngine[] }

export function SerialNumbers() {
  const { sap = '' } = useParams();
  const [params] = useSearchParams();
  const { activeSeason } = useSeason();
  const season = params.get('season') || activeSeason;
  const { can } = useAuth();
  const canWrite = can('production:write');
  const df = useDateFormatter();
  const [confirmNode, confirm] = useConfirm();

  // the vessel's name and model from the Overview when its reader may see it (Info / Files readers may not),
  // else from the Order Sheet or the link
  const canOverview = can('overview:read');
  const overview = useOverview(season || undefined, { enabled: canOverview });
  const sheetQ = useOrderSheet();
  const snQ = useSnVessels();
  const saved = snQ.data?.index[sap];
  const textQ = useSnText(saved?.key);
  const vessel = overview.data?.vessels.find((v) => v.sap === sap);
  const row = sheetQ.data?.sheet[sap];
  const name = vessel?.name ?? row?.[OS_NAME_KEY] ?? params.get('name') ?? '';
  const rawModel = vessel?.model ?? row?.[OS_MODEL_KEY] ?? params.get('model') ?? '';
  const model = rawModel ? modelKey(rawModel) : '';

  // the form: the saved file read back, else a new one from the order
  const [form, setForm] = useState<Form | null>(null);
  const [unread, setUnread] = useState(false); // a saved file that doesn't read as one
  const [dirty, setDirty] = useState(false);
  const overviewDone = !canOverview || !!overview.data || !!overview.error;
  const ready = Boolean(overviewDone && sheetQ.data && snQ.data && (!saved || textQ.data != null));
  const fresh = () => snFromOrder({ sap, vesselName: name, model, order: row ?? null });
  useEffect(() => {
    if (!ready || form) return;
    const back = saved ? parseSnText(textQ.data ?? '') : null;
    if (back) {
      const brand: Form['brand'] = back.devices.some((d) => /GARMIN/i.test(d.label)) ? 'GARMIN' : back.devices.some((d) => /RAYMARINE|AXIOM/i.test(d.label)) ? 'RAYMARINE' : '';
      setForm({ model: back.model || model, vesselName: back.vesselName || name, brand, devices: back.devices, engines: back.engines });
    } else {
      const f = fresh();
      setForm({ model: f.model, vesselName: f.vesselName, brand: f.brand, devices: f.devices, engines: f.engines });
      setUnread(Boolean(saved));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, form, saved, textQ.data]);
  const started = useMemo(() => (form && !saved ? fresh().fromOrder : false), [form, saved]); // eslint-disable-line react-hooks/exhaustive-deps

  const text = form ? buildSnText({ sap, model: form.model, vesselName: form.vesselName, devices: form.devices, engines: form.engines }) : '';
  const fileName = snFileName(sap);
  // where it is saved: beside the saved file, else in the vessel's folder, else a new folder in the season's year
  const folder = saved ? saved.key.slice(0, saved.key.lastIndexOf('/') + 1)
    : snQ.data?.folders[sap] ?? `${infocenterYear(season)}${vesselFolderName(sap, name || form?.vesselName || '')}/`;
  const where = folder.replace(/^infocenter\//, '').replace(/\/$/, '').split('/').join(' › ');

  const change = (p: Partial<Form>) => { setForm((f) => (f ? { ...f, ...p } : f)); setDirty(true); };
  const setDev = (key: string, p: Partial<SnDevice>) => form && change({ devices: form.devices.map((d) => (d.key === key ? { ...d, ...p } : d)) });
  const setEng = (key: string, p: Partial<SnEngine>) => form && change({ engines: form.engines.map((e) => (e.key === key ? { ...e, ...p } : e)) });
  const switchBrand = async (b: SnBrand) => {
    if (!form || form.brand === b) return;
    if (form.devices.some((d) => d.serial.trim()) && !(await confirm({ title: `Switch to ${b}`, message: `The devices are replaced with ${b}’s, and the serials typed for them go. The engines stay.`, confirmLabel: 'Switch' }))) return;
    change({ brand: b, devices: snFromOrder({ sap, vesselName: form.vesselName, model: form.model, order: row ?? null, brand: b }).devices });
  };

  const save = useSaveSnFile();
  const [msg, setMsg] = useState('');
  const onSave = async () => {
    if (!form) return;
    setMsg('');
    await save.mutateAsync({ folder, sap, text });
    setDirty(false);
    setUnread(false);
    setMsg(`Saved ${fileName} in Info / Files › Infocenter › ${where}.`);
  };
  const onDownload = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  const back = <Back folder={saved || snQ.data?.folders[sap] ? folder : ''} />;
  const err = sheetQ.error ?? snQ.error ?? textQ.error;
  if (err) return <>{back}<ErrorState error={err} /></>;
  if (!form) return <>{back}<LoadingState /></>;

  const ro = !canWrite;
  return (
    <>
      {back}
      <PageHeader title={`Serial numbers · ${form.vesselName || name || sap}`} meta={<span className="muted">{form.model || model || 'No model'} · SAP {sap} · season {season}</span>}>
        {dirty && <span className="pill warn" title="Changes not saved yet">Not saved</span>}
        <button className="btn btn-sm" onClick={onDownload} title="The file as it stands, without saving"><IconDownload size={14} /> Download</button>
        {canWrite && <button className="btn btn-sm btn-primary" disabled={save.isPending} onClick={() => void onSave()}>{save.isPending ? 'Saving…' : `Save ${fileName}`}</button>}
      </PageHeader>
      <div className="muted" role="status" style={{ marginBottom: 10 }}>
        {msg ? <>{msg} <Link to={folderLink(folder)}>Open the folder</Link></> : (saved
          ? <>{fileName} in <Link to={folderLink(folder)}>Infocenter › {where}</Link>, saved {df.dateTime(saved.uploaded)}.</>
          : <>No {fileName} yet: {started ? 'started from the boat’s order' : 'the yard’s standard list'}. Saving puts it in {snQ.data?.folders[sap] ? <Link to={folderLink(folder)}>Infocenter › {where}</Link> : `Infocenter › ${where}`}.</>)}
      </div>
      {unread && <p className="sn-note">The saved {fileName} doesn’t read as a serial-numbers file, so the form starts from the order; it is shown below as it is. Saving replaces it.</p>}
      {save.error && <InlineError error={save.error} onDismiss={() => save.reset()} />}

      <div className="sn-layout">
        <section className="card sn-form" aria-label="Serial numbers">
          <div className="sn-ids">
            <label><span className="field-label">Model</span><input className="field" value={form.model} disabled={ro} onChange={(e) => change({ model: e.target.value })} /></label>
            <label><span className="field-label">Vessel / client</span><input className="field" value={form.vesselName} disabled={ro} onChange={(e) => change({ vesselName: e.target.value })} /></label>
            <label><span className="field-label">SAP</span><input className="field" value={sap} disabled /></label>
          </div>

          <div className="sn-head">
            <h2>Electronics</h2>
            {canWrite && (
              <div className="sn-brands" role="group" aria-label="Brand">
                {(['RAYMARINE', 'GARMIN'] as SnBrand[]).map((b) => (
                  <button key={b} type="button" className={`btn btn-sm ${form.brand === b ? 'btn-primary' : 'btn-ghost'}`} aria-pressed={form.brand === b} onClick={() => void switchBrand(b)}>{b}</button>
                ))}
              </div>
            )}
            {canWrite && <button type="button" className="btn btn-sm" onClick={() => change({ devices: [...form.devices, { key: snKey(), label: '', partNo: '', serial: '' }] })}>+ Device</button>}
          </div>
          <table className="tbl sn-tbl sn-dev">
            <thead><tr><th>Device</th><th className="sn-part">Part no.</th><th className="sn-serial">Serial</th>{canWrite && <th className="sn-x" />}</tr></thead>
            <tbody>
              {form.devices.map((d, i) => (
                <tr key={d.key}>
                  <td><input className="field" aria-label={`Device ${i + 1}`} value={d.label} placeholder="Device" disabled={ro} onChange={(e) => setDev(d.key, { label: e.target.value })} /></td>
                  <td><input className="field" aria-label={`Device ${i + 1} part number`} value={d.partNo} placeholder="Part no." disabled={ro} onChange={(e) => setDev(d.key, { partNo: e.target.value })} /></td>
                  <td><input className="field mono" aria-label={`${d.label || `Device ${i + 1}`} serial`} value={d.serial} placeholder="Serial" disabled={ro} onChange={(e) => setDev(d.key, { serial: e.target.value })} /></td>
                  {canWrite && <td><button type="button" className="btn btn-sm btn-ghost btn-del" aria-label={`Remove ${d.label || `device ${i + 1}`}`} onClick={() => change({ devices: form.devices.filter((x) => x.key !== d.key) })}><IconTrash size={14} /></button></td>}
                </tr>
              ))}
              {!form.devices.length && <tr><td colSpan={4} className="muted">No devices.</td></tr>}
            </tbody>
          </table>

          <div className="sn-head">
            <h2>Engines</h2>
            {canWrite && <button type="button" className="btn btn-sm" onClick={() => change({ engines: [...form.engines, newEngine(form.engines[form.engines.length - 1]?.maker, form.engines[form.engines.length - 1]?.hp)] })}>+ Engine</button>}
          </div>
          <table className="tbl sn-tbl sn-eng">
            <thead><tr><th>Engine</th><th>Make</th><th className="sn-part">Part no.</th><th className="sn-serial">Serial</th>{canWrite && <th className="sn-x" />}</tr></thead>
            <tbody>
              {form.engines.map((e, i) => (
                <tr key={e.key}>
                  <td><input className="field" aria-label={`Engine ${i + 1}`} value={e.hp} placeholder="F150, 300 …" disabled={ro} onChange={(ev) => setEng(e.key, { hp: ev.target.value })} /></td>
                  <td>
                    <select className="field" aria-label={`Engine ${i + 1} make`} value={e.maker} disabled={ro} onChange={(ev) => setEng(e.key, { maker: ev.target.value })}>
                      {[...new Set([...ENGINE_MAKERS, e.maker])].map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </td>
                  <td><input className="field" aria-label={`Engine ${i + 1} part number`} value={e.partNo} placeholder="Part no." disabled={ro} onChange={(ev) => setEng(e.key, { partNo: ev.target.value })} /></td>
                  <td><input className="field mono" aria-label={`Engine ${i + 1} serial`} value={e.serial} placeholder="Serial" disabled={ro} onChange={(ev) => setEng(e.key, { serial: ev.target.value })} /></td>
                  {canWrite && <td><button type="button" className="btn btn-sm btn-ghost btn-del" aria-label={`Remove engine ${i + 1}`} onClick={() => change({ engines: form.engines.filter((x) => x.key !== e.key) })}><IconTrash size={14} /></button></td>}
                </tr>
              ))}
              {!form.engines.length && <tr><td colSpan={5} className="muted">No engines.</td></tr>}
            </tbody>
          </table>
        </section>

        <aside className="card sn-file" aria-label={fileName}>
          <h2>{fileName}</h2>
          <pre className="sn-pre">{text}</pre>
          {unread && textQ.data != null && (
            <>
              <h2>Saved now</h2>
              <pre className="sn-pre">{textQ.data}</pre>
            </>
          )}
        </aside>
      </div>
      {confirmNode}
    </>
  );
}

/** Info / Files › Infocenter at a folder ('' for its top) */
const folderLink = (folder: string) => `/info-files?in=infocenter${folder ? `&path=${encodeURIComponent(folder)}` : ''}`;

function Back({ folder }: { folder: string }) {
  return (
    <Link to={folderLink(folder)} className="adm-back">
      <IconChevronRight size={16} strokeWidth={2.2} />
      Infocenter
    </Link>
  );
}
